<template>
  <!--
    非常勤・パートの働き方。登録画面と修正画面で**同じ部品を使う**。

    別々に書くと必ずずれる。休日出勤報告が新規・修正・詳細で3通りの
    扱いになっていた（2026-08-17 に1つへまとめた）。
  -->
  <div class="work-pattern">
    <CRow>
      <CCol sm="12" md="8" lg="6">
        <label>週稼働日数（有休付与区分）</label>
        <div class="paid-grant-radio">
          <label
            class="btn btn-outline-info btn-sm paid-grant-btn"
            :class="{ active: workingDays === 5 }"
            for="paidGrantNormal"
          >
            <input
              type="radio"
              id="paidGrantNormal"
              name="paidGrantRule"
              :value="5"
              :checked="workingDays === 5"
              @change="selectWorkingDays(5)"
            />
            通常(週5日)
          </label>
          <label
            v-for="days in [4, 3, 2, 1]"
            :key="days"
            class="btn btn-outline-info btn-sm paid-grant-btn"
            :class="{ active: workingDays === days }"
            :for="'paidGrant' + days"
          >
            <input
              type="radio"
              :id="'paidGrant' + days"
              name="paidGrantRule"
              :value="days"
              :checked="workingDays === days"
              @change="selectWorkingDays(days)"
            />
            週{{ days }}日
          </label>
          <label
            class="btn btn-outline-secondary btn-sm paid-grant-btn"
            :class="{ active: workingDays === 0 }"
            for="paidGrantNone"
          >
            <input
              type="radio"
              id="paidGrantNone"
              name="paidGrantRule"
              :value="0"
              :checked="workingDays === 0"
              @change="selectWorkingDays(0)"
            />
            有休付与無し
          </label>
        </div>
      </CCol>
    </CRow>

    <!--
      週4日以下のときだけ聞く。通常(週5日)と有休付与無しは会社カレンダー
      どおりに働くので、曜日も所定労働時間も要らない。
    -->
    <template v-if="isPartTime">
      <CRow class="mt-3">
        <CCol sm="12" md="10" lg="8">
          <label>稼働曜日<span class="asterisk">*</span></label>
          <div class="work-pattern__weekdays">
            <label
              v-for="day in WEEKDAYS"
              :key="day.iso"
              class="btn btn-outline-info btn-sm work-pattern__weekday"
              :class="{ active: isWorkingWeekday(day.iso) }"
              :for="'workWeekday' + day.iso"
            >
              <input
                type="checkbox"
                :id="'workWeekday' + day.iso"
                :checked="isWorkingWeekday(day.iso)"
                @change="toggleWeekday(day.iso)"
              />
              {{ day.label }}
            </label>
          </div>
          <p v-if="weekdayError" class="error">
            <span class="error">{{ weekdayError }}</span>
          </p>
          <div class="text-muted small mt-1">
            出勤予定かどうかの判定に使います。選んでいない曜日は「休日」になり、
            今日の勤怠状況にも並びません。
          </div>
        </CCol>
      </CRow>

      <CRow class="mt-3">
        <CCol sm="12" md="6" lg="4">
          <label>1日あたりの所定労働時間<span class="asterisk">*</span></label>
          <div class="work-pattern__hours">
            <input
              type="number"
              class="form-control preika-form-control"
              min="0"
              max="24"
              step="0.5"
              :value="dailyHours"
              @input="changeHours($event.target.value)"
            />
            <span class="work-pattern__unit">時間</span>
          </div>
          <p v-if="hoursError" class="error">
            <span class="error">{{ hoursError }}</span>
          </p>
          <!--
            ここを聞くのは法令の要件だから。「週4日以下」だけでは
            比例付与にならない。
          -->
          <div class="text-muted small mt-1">
            週の所定労働時間は<strong>「この時間 × 週稼働日数」</strong>で出します。
            <strong>週30時間以上なら通常(週5日)と同じ日数</strong>が付与されます。
            比例付与は「週4日以下 かつ 週30時間未満」が要件のためです（労基則第24条の3）。
          </div>
        </CCol>
      </CRow>

      <CRow class="mt-2">
        <CCol sm="12">
          <div class="work-pattern__grant" :class="{ 'is-proportional': isProportional }">
            この設定での付与区分：<strong>{{ grantLabel }}</strong>
          </div>
        </CCol>
      </CRow>
    </template>
  </div>
</template>

<script>
/** 1=月 〜 7=日。会社の週休（t_account_weekly_holiday）と同じ番号にそろえる。 */
const WEEKDAYS = [
  { iso: 1, label: '月' },
  { iso: 2, label: '火' },
  { iso: 3, label: '水' },
  { iso: 4, label: '木' },
  { iso: 5, label: '金' },
  { iso: 6, label: '土' },
  { iso: 7, label: '日' }
]

/**
 * 比例付与の上限。週30時間 = 1800分。
 *
 * **1日あたりではなく週で見る。** 要件は「週所定労働時間30時間未満」。
 * 1日4時間でも週5日なら20時間だが、週4日×8時間は32時間で通常付与になる。
 */
const WEEKLY_PROPORTIONAL_LIMIT_MINUTES = 1800

export default {
  name: 'WorkPatternInput',
  props: {
    workingDays: { type: Number, default: 5 },
    /** '1010100' のような7文字。未設定は空文字。 */
    workWeekdays: { type: String, default: '' },
    /** 1日あたりの所定労働時間（分）。未設定は空文字。 */
    dailyWorkMinutes: { type: String, default: '' }
  },
  data () {
    return { WEEKDAYS }
  },
  computed: {
    isPartTime () {
      return this.workingDays >= 1 && this.workingDays <= 4
    },
    selectedCount () {
      return (this.workWeekdays || '').split('').filter(c => c === '1').length
    },
    dailyHours () {
      const minutes = Number(this.dailyWorkMinutes)
      if (!this.dailyWorkMinutes || !Number.isFinite(minutes)) return ''
      return Math.round((minutes / 60) * 100) / 100
    },
    isProportional () {
      if (!this.isPartTime) return false
      const minutes = Number(this.dailyWorkMinutes)
      if (!this.dailyWorkMinutes || !Number.isFinite(minutes)) return false
      // 週の所定労働時間 = 1日あたり × 週稼働日数
      return minutes * this.workingDays < WEEKLY_PROPORTIONAL_LIMIT_MINUTES
    },
    grantLabel () {
      if (!this.isPartTime) return '通常(週5日)'
      if (!this.dailyWorkMinutes) return '1日あたりの所定労働時間を入力してください'
      return this.isProportional
        ? `比例付与（週${this.workingDays}日）`
        : '通常(週5日)と同じ ※週30時間以上のため'
    },
    weekdayError () {
      if (!this.isPartTime) return ''
      if (this.selectedCount === 0) return '稼働曜日を選んでください。'
      if (this.selectedCount !== this.workingDays) {
        return `勤務日数が週${this.workingDays}日に設定されているため、曜日は${this.workingDays}つ選択してください（現在${this.selectedCount}つ選択中）。`
      }
      return ''
    },
    hoursError () {
      if (!this.isPartTime) return ''
      if (!this.dailyWorkMinutes) return '1日あたりの所定労働時間を入力してください。'
      const minutes = Number(this.dailyWorkMinutes)
      if (!Number.isFinite(minutes) || minutes <= 0) {
        return '1日あたりの所定労働時間を数値で入力してください。'
      }
      if (minutes > 1440) return '1日あたりの所定労働時間が24時間を超えています。'
      return ''
    },
    invalid () {
      return Boolean(this.weekdayError || this.hoursError)
    }
  },
  watch: {
    invalid: {
      immediate: true,
      handler (value) {
        this.$emit('validity', !value)
      }
    }
  },
  methods: {
    isWorkingWeekday (iso) {
      return (this.workWeekdays || '').charAt(iso - 1) === '1'
    },
    toggleWeekday (iso) {
      const current = ((this.workWeekdays || '').length === 7
        ? this.workWeekdays
        : '0000000').split('')
      current[iso - 1] = current[iso - 1] === '1' ? '0' : '1'
      this.$emit('update:workWeekdays', current.join(''))
    },
    changeHours (value) {
      const hours = Number(value)
      if (value === '' || !Number.isFinite(hours)) {
        this.$emit('update:dailyWorkMinutes', '')
        return
      }
      this.$emit('update:dailyWorkMinutes', String(Math.round(hours * 60)))
    },
    selectWorkingDays (days) {
      this.$emit('update:workingDays', days)
      /*
       * 通常(週5日)と有休付与無しへ戻したら、曜日と所定労働時間を消す。
       *
       * 残しておくと「週5日なのに月水金だけ稼働」という辻褄の合わない
       * 状態がDBに残る。空文字は「未設定へ戻す」の意味で、サーバ側が
       * NULL にする（送らないのとは別）。
       */
      if (days < 1 || days > 4) {
        this.$emit('update:workWeekdays', '')
        this.$emit('update:dailyWorkMinutes', '')
      }
    }
  }
}
</script>

<style scoped>
.work-pattern__weekdays {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.work-pattern__weekday {
  min-width: 44px;
  cursor: pointer;
}

.work-pattern__weekday input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.work-pattern__hours {
  display: flex;
  align-items: center;
  gap: 8px;
}

.work-pattern__hours input {
  max-width: 120px;
}

.work-pattern__unit {
  color: #6a7f8c;
  font-size: 13px;
}

.work-pattern__grant {
  display: inline-block;
  padding: 6px 12px;
  border: 1px solid #dfe7ed;
  border-radius: 8px;
  background: #f5f9fb;
  color: #28495d;
  font-size: 13px;
}

.work-pattern__grant.is-proportional {
  border-color: #c6e2f3;
  background: #e7f3fb;
}
</style>
